import { ReactElement, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import classNames from 'classnames';
import Input from '~/ui/components/inputs/Input';
import AutoSubmit from '~/ui/components/inputs/AutoSubmit';
import DatePicker from '~/ui/components/inputs/DatePicker';
import styles from './Header.module.scss';
import { getInitialValues } from './form';
import { TriageType } from '~/services/api/enums';
import { ITriageRequestsFilters } from '~/services/api/triage/types';
import SelectVirtualized from '~/ui/components/inputs/SelectVirtualized';
import api from '~/services/api';
import { IIdName } from '~/services/api/types';
import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';

const colors = {
  [TriageType.WaitingReply]: 'red',
  [TriageType.Completed]: 'green',
};

interface IProps {
  programId: number;
  triageTypeId: TriageType;
  total: number;
  onSubmit: (filters: ITriageRequestsFilters) => void;
}

const Header = ({ programId, triageTypeId, total, onSubmit }: IProps): ReactElement => {
  const [isLoading, setIsLoading] = useState(true);
  const [programs, setPrograms] = useState<IIdName[]>([]);

  const showError = useStoreActions(actions => actions.snackbar.showError);

  const initialValues = getInitialValues();

  const formMethods = useForm<ITriageRequestsFilters>({
    defaultValues: initialValues,
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = formMethods;

  const programOptions = useMemo(() => {
    const options = programs.map(item => ({
      value: item.id,
      label: item.name,
    }));

    return [
      {
        value: '',
        label: 'All Programs',
      },
      ...options,
    ];
  }, [programs.length]);

  const handleOnSubmit = (values: ITriageRequestsFilters) => {
    onSubmit(values);
  };

  const onMount = async () => {
    if (programId !== null) {
      return;
    }

    setIsLoading(true);

    try {
      const selectors = await api.triage.getSelectors().then(res => res.data);
      setPrograms(selectors.programs);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    onMount();
  }, []);

  return (
    <FormProvider {...formMethods}>
      <form className={styles.container} onSubmit={handleSubmit(handleOnSubmit)}>
        <Grid container spacing={2} alignItems="center">
          {programId === null && (
            <Grid item xs>
              <SelectVirtualized
                name="programId"
                control={control}
                errors={errors}
                options={programOptions}
                isLoading={isLoading}
                label="Program Name"
              />
            </Grid>
          )}
          <Grid item xs>
            <Input name="patientFullName" label="Patient" register={register} errors={errors} />
          </Grid>
          <Grid item xs>
            <Input name="issueName" label="Issue" register={register} errors={errors} />
          </Grid>
          <Grid item xs>
            <DatePicker
              placeholder=".. /.. /.."
              label="Start Date"
              name="startDate"
              maxDate={new Date()}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item xs>
            <DatePicker
              placeholder=".. /.. /.."
              label="End Date"
              name="endDate"
              maxDate={new Date()}
              control={control}
              errors={errors}
            />
          </Grid>
          <Grid item>
            <Grid
              container
              className={classNames(styles.total, styles[colors[triageTypeId] ?? 'blue'])}
              alignItems="center"
              justifyContent="center"
            >
              <Grid item>{total}</Grid>
            </Grid>
          </Grid>
          <AutoSubmit debounce={1000} initialValues={initialValues} onSubmit={handleOnSubmit} />
        </Grid>
      </form>
    </FormProvider>
  );
};

export default Header;
